import React, { useState, useEffect, useRef } from 'react';
import { MathJax, MathJaxContext } from 'better-react-mathjax';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { Sparkles, Send, Mic, MicOff, Bot, User, Maximize2, Minimize2 } from 'lucide-react';
import { askGPT } from './OpenAIService';
import mathJaxConfig from './mathJaxConfig';

const systemPrompt = "Tu es un assistant éducatif. Réponds de manière claire et concise, avec des exemples adaptés au niveau de l'étudiant.";

const ChatWithGPT = () => {
  const [inputText, setInputText] = useState('');
  const [chatLog, setChatLog] = useState([]);
  const [isListening, setIsListening] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const chatContainerRef = useRef(null);
  const { transcript, resetTranscript } = useSpeechRecognition();

  // Effet pour le défilement automatique
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatLog]);

  // Effet pour MathJax
  useEffect(() => {
    const typesetMath = async () => {
      try {
        if (window.MathJax) {
          await window.MathJax.typesetPromise();
        }
      } catch (err) {
        console.error('Erreur lors du rendu MathJax :', err);
      }
    };

    if (chatLog.length > 0) {
      typesetMath();
    }
  }, [chatLog]);

  // Effet pour la reconnaissance vocale
  useEffect(() => {
    if (transcript) {
      setInputText(transcript);
    }
  }, [transcript]);

  const handleSendText = async () => {
    if (!inputText.trim() || isLoading) return;

    const userMessage = inputText.trim();
    const message = `${systemPrompt}
            Tu es un coach virtuel spécialisé en développement personnel et éducatif. Ton rôle est d’aider l’utilisateur à définir un objectif clair en suivant les étapes suivantes : explorer l’objectif, le clarifier, le décomposer en étapes concrètes, identifier les obstacles, et créer un plan d’action.
            Utilise uniquement les caractères $...$ pour encadrer les formules mathématiques en ligne et $$...$$ pour encadrer les formules en mode affiché.
            Ne pas utiliser les caractères spéciaux \\ ( ) [ ] pour encadrer les formules mathématiques. 
            Limite ta réponse à 100 mots.
            Voici le texte : ${userMessage}`;

    setIsLoading(true);
    setChatLog(prev => [...prev, { role: 'user', content: userMessage }]);
    setInputText('');
    resetTranscript();

    try {
      const response = await askGPT(message);
      setChatLog(prev => [...prev, { role: 'assistant', content: response }]);
    } catch (error) {
      console.error("Erreur lors de l'appel à OpenAI:", error);
      setChatLog(prev => [...prev, { 
        role: 'assistant', 
        content: "Désolé, je n'ai pas pu obtenir de réponse pour le moment." 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendText();
    }
  };

  const toggleListening = () => {
    if (isListening) {
      SpeechRecognition.stopListening();
      setIsListening(false);
    } else {
      setIsListening(true);
      SpeechRecognition.startListening();
    }
  };

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  return (
    <MathJaxContext config={mathJaxConfig}>
      <div className={`flex flex-col transition-all duration-300 ease-in-out
        ${isMaximized ? 'fixed inset-0 z-50' : 'h-full w-full'}`}>
        <div className="flex-1 bg-white rounded-lg shadow overflow-hidden flex flex-col">
          {/* Header avec bouton de maximisation */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-white">
                <Bot size={24} />
                <h2 className="text-xl font-semibold">Assistant Aladinn</h2>
              </div>
              <button 
                onClick={toggleMaximize}
                className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
              >
                {isMaximized ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
              </button>
            </div>
          </div>

          {/* Chat Container avec hauteur dynamique */}
          <div 
            ref={chatContainerRef}
            className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50"
          >
            {/* Welcome message */}
            {chatLog.length === 0 && (
              <div className="flex justify-center items-center h-full text-center text-gray-500">
                <div className="space-y-4">
                  <Sparkles className="h-12 w-12 mx-auto text-blue-500" />
                  <p className="text-lg">
                    Bonjour ! Je suis votre assistant personnel Aladinn.
                    <br />
                    Comment puis-je vous aider aujourd'hui ?
                  </p>
                </div>
              </div>
            )}

            {/* Messages */}
            {chatLog.map((message, index) => (
              <div
                key={index}
                className={`flex items-start gap-3 ${
                  message.role === 'assistant' ? 'justify-start' : 'justify-end'
                }`}
              >
                {message.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center flex-shrink-0">
                    <Bot size={20} className="text-white" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] p-3 rounded-lg ${
                    message.role === 'assistant'
                      ? 'bg-white'
                      : 'bg-blue-600 text-white'
                  }`}
                >
                  <MathJax>
                    <div className="prose prose-sm max-w-none">
                      {message.content}
                    </div>
                  </MathJax>
                </div>
                {message.role === 'user' && (
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                    <User size={20} className="text-white" />
                  </div>
                )}
              </div>
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center">
                  <Bot size={20} className="text-white" />
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <div className="flex gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-600 animate-bounce"></div>
                    <div className="w-2 h-2 rounded-full bg-blue-600 animate-bounce delay-100"></div>
                    <div className="w-2 h-2 rounded-full bg-blue-600 animate-bounce delay-200"></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input Area */}
          <div className="p-4 bg-white border-t">
            <div className="flex gap-2">
              <button
                onClick={toggleListening}
                className={`p-2 rounded-lg transition-colors ${
                  isListening 
                    ? 'bg-red-100 text-red-600 hover:bg-red-200' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {isListening ? <MicOff size={20} /> : <Mic size={20} />}
              </button>
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Posez votre question..."
                className="flex-grow p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={1}
              />
              <button
                onClick={handleSendText}
                disabled={!inputText.trim() || isLoading}
                className={`p-2 rounded-lg transition-colors ${
                  inputText.trim() && !isLoading
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                <Send size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </MathJaxContext>
  );
};

export default ChatWithGPT;